import { forwardRef, useRef, useCallback } from 'react';
import { motion, useInView } from 'framer-motion';
import { ScrollStackReveal } from './ScrollStackReveal';

function assignRef(ref, node) {
  if (!ref) return;
  if (typeof ref === 'function') ref(node);
  else ref.current = node;
}

/**
 * Intro + stack on a continuous white surface (matches light portfolio stacks like Framer references).
 */
export const FeaturedStackingShowcase = forwardRef(function FeaturedStackingShowcase(
  {
    items,
    kicker = '',
    heading = 'PROJECTS & INVESTMENTS',
    intro,
    dark = false,
    className = '',
  },
  forwardedRef
) {
  const rootRef = useRef(null);

  const setRefs = useCallback(
    (node) => {
      rootRef.current = node;
      assignRef(forwardedRef, node);
    },
    [forwardedRef]
  );

  const inView = useInView(rootRef, { once: true, margin: '-40px' });

  return (
    <section
      ref={setRefs}
      className={className}
      style={{ position: 'relative', width: '100%' }}
      aria-labelledby="featured-stacking-heading"
    >
      <div style={{ background: dark ? '#0f1014' : '#fff', color: dark ? '#f5f5f5' : '#171717' }}>
        <div style={{ margin: '0 auto', maxWidth: 1280, padding: '56px clamp(20px,4vw,48px) 14px' }}>
          <motion.p
            style={{
              marginBottom: 8,
              fontFamily: "'DM Sans', system-ui, sans-serif",
              fontSize: 11,
              fontWeight: 500,
              textTransform: 'uppercase',
              letterSpacing: '0.2em',
              color: dark ? '#a3a3a3' : '#737373',
            }}
            initial={{ opacity: 0, y: 8 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.45 }}
          >
            {kicker}
          </motion.p>
          <motion.h2
            id="featured-stacking-heading"
            style={{
              fontSize: 'clamp(2.5rem, 7vw, 4.5rem)',
              fontWeight: 400,
              lineHeight: 0.92,
              letterSpacing: '0.02em',
              color: dark ? '#f5f5f5' : '#0a0a0a',
              fontFamily: "'Bebas Neue', system-ui, sans-serif",
            }}
            initial={{ opacity: 0, y: 18 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.55, delay: 0.04 }}
          >
            {heading}
          </motion.h2>
          {intro ? (
            <motion.p
              style={{
                marginTop: 16,
                maxWidth: 740,
                fontFamily: "'DM Sans', system-ui, sans-serif",
                fontSize: 15,
                fontWeight: 300,
                lineHeight: 1.6,
                color: dark ? '#d4d4d4' : '#525252',
              }}
              initial={{ opacity: 0, y: 12 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              {intro}
            </motion.p>
          ) : null}
        </div>
      </div>

      {/* Single scroll driver + sticky stage — layers overlap, no vertical gaps */}
      <div style={{ position: 'relative', width: '100%', background: dark ? '#0f1014' : '#fff' }}>
        <ScrollStackReveal items={items} dark={dark} />
      </div>
    </section>
  );
});

FeaturedStackingShowcase.displayName = 'FeaturedStackingShowcase';
